import React, { useCallback, useMemo } from "react";
import "../home-styling/Dashboard.css";

const Dashboard = ({
  dashboardPackages,
  packages,
  bookingStats,
  financeData,
  loading,
  handleCreatePackage,
  handleViewAll,
  viewPackageDetails,
  formatCurrency,
  handleViewBookings,
  handleViewEarnings,
  reviews = [],
  handleViewReviews,
  isVerified = false,
}) => {
  // Memoized data transformations
  const displayPackages = useMemo(() => 
    dashboardPackages.slice(0, 2), 
    [dashboardPackages]
  );

  const displayReviews = useMemo(() => 
    reviews.slice(0, 2), 
    [reviews]
  );

  // Memoized utility functions
  const safeToFixed = useCallback((value, decimals = 1) => {
    const num = parseFloat(value);
    return isNaN(num) ? '0.0' : num.toFixed(decimals);
  }, []);

  const safeRound = useCallback((value) => {
    const num = parseFloat(value);
    return isNaN(num) ? 0 : Math.round(num);
  }, []);

  const renderStars = useCallback((rating, size = "small") => {
    const starSize = size === "large" ? "18px" : "14px";
    return Array.from({ length: 5 }, (_, index) => (
      <span
        key={index}
        className={`star ${index < rating ? "filled" : "empty"}`}
        style={{ 
          fontSize: starSize, 
          color: index < rating ? '#ffc107' : '#ddd' 
        }}
      >
        {index < rating ? "★" : "☆"}
      </span>
    ));
  }, []);

  const safeFormatDate = useCallback((dateString) => {
    if (!dateString) return "Not specified";
    try {
      return new Date(dateString).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      });
    } catch (error) {
      return "Invalid date";
    }
  }, []);

  // Memoized event handlers - SIMPLIFIED
  const handleCreatePackageClick = useCallback(() => {
    if (!isVerified) {
      // Do nothing - just show the disabled state
      return;
    }
    handleCreatePackage();
  }, [isVerified, handleCreatePackage]);

  const handlePackageClick = useCallback((packageId) => {
    viewPackageDetails(packageId);
  }, [viewPackageDetails]);

  const handleViewAllClick = useCallback((e) => {
    e.preventDefault();
    handleViewAll(e);
  }, [handleViewAll]);

  const handleViewBookingsClick = useCallback((e) => {
    e.preventDefault();
    handleViewBookings(e);
  }, [handleViewBookings]);

  const handleViewEarningsClick = useCallback((e) => {
    e.preventDefault();
    handleViewEarnings(e);
  }, [handleViewEarnings]);

  const handleViewReviewsClick = useCallback((e) => {
    e.preventDefault();
    handleViewReviews?.(e);
  }, [handleViewReviews]);

  // Memoized render functions - SIMPLIFIED
  const renderCreatePackageSection = useMemo(() => {
    const createPackageClass = `create-package ${!isVerified ? 'get-verified disabled' : ''}`;
    
    return (
      <div className={createPackageClass} onClick={handleCreatePackageClick}>
        <h2>{isVerified ? "Create Package" : "Get Verified"}</h2>
        <div className={isVerified ? "plus-icon" : "verification-icon"}>
          {isVerified ? "+" : "🔒"}
        </div>
        <p>
          {isVerified 
            ? "Start creating your own package" 
            : "Account verification required to create packages"
          }
        </p>
      </div>
    );
  }, [isVerified, handleCreatePackageClick]);

  const renderDashboardPackages = useMemo(() => {
    if (loading.dashboard) {
      return <div className="loading">Loading packages...</div>;
    }

    if (displayPackages.length === 0) {
      return (
        <div className="no-packages-dashboard">
          <p>{isVerified ? "No packages created yet." : "No packages available"}</p>
          <p>
            {isVerified 
              ? "Click the + icon to create your first package!" 
              : "Get verified to create packages"
            }
          </p>
        </div>
      );
    }

    return displayPackages.map((pkg) => {
      const packageLogo = pkg.logo_url || pkg.logoDataURL;
      const isValidImage = packageLogo && packageLogo.length > 0;

      return (
        <div
          key={pkg.id}
          className="dashboard-package-card"
          onClick={() => handlePackageClick(pkg.id)}
        >
          {isValidImage && (
            <div className="dashboard-package-image">
              <img
                src={packageLogo}
                alt={pkg.packageName || "Package Logo"}
                loading="lazy"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            </div>
          )}

          <div className="dashboard-package-header">
            <h4>{pkg.packageName || "Untitled Package"}</h4>
          </div>

          <div className="dashboard-package-meta">
            <span className="dashboard-meta-item">
              • {pkg.primaryLocation || "No location"}
            </span>
            <span className="dashboard-meta-item">
              • {pkg.numPeople ? pkg.numPeople + " People" : "No people info"}
            </span>
            <span className="dashboard-meta-item">
              • {pkg.price ? "₹" + pkg.price : "Price not set"}
            </span>
          </div>

          <p className="dashboard-package-description">
            {pkg.shortDescription || "No description provided"}
          </p>

          <div className="dashboard-package-footer">
            <small>
              Created:{" "}
              {pkg.createdAt
                ? new Date(pkg.createdAt).toLocaleDateString()
                : "Unknown"}
            </small>
            <span className="click-hint">Click to preview details</span>
          </div>
        </div>
      );
    });
  }, [displayPackages, loading.dashboard, isVerified, handlePackageClick]);

  const renderDashboardReviews = useMemo(() => {
    if (loading.reviews) {
      return <div className="loading">Loading reviews...</div>;
    }

    if (displayReviews.length === 0) {
      return (
        <div className="no-reviews-dashboard">
          <p>No reviews yet.</p>
          <p>Reviews will appear here once customers start rating your packages.</p>
        </div>
      );
    }

    return displayReviews.map((review) => (
      <div key={review.id} className="dashboard-review-card">
        <div className="review-header">
          <div className="customer-avatar">
            <div className="avatar-placeholder">
              {review.user_id?.charAt(0).toUpperCase() || "U"}
            </div>
          </div>
          <div className="review-info">
            <div className="review-rating">
              {renderStars(safeRound(review.rating_overall))}
              <span className="rating-score">
                {safeToFixed(review.rating_overall)}
              </span>
            </div>
            <div className="review-meta">
              <span className="package-name">
                {review.package_name || "Unknown Package"}
              </span>
              <span className="review-date">
                {safeFormatDate(review.created_at)}
              </span>
            </div>
          </div>
        </div>
        
        <div className="review-content">
          <p className="review-comment">
            {review.comment || "No comment provided"}
          </p>
        </div>
      </div>
    ));
  }, [displayReviews, loading.reviews, renderStars, safeRound, safeToFixed, safeFormatDate]);

  // Memoized view more links
  const renderViewMorePackages = useMemo(() => {
    if (packages.length <= 2) return null;
    
    return (
      <div className="view-more-container">
        <a
          href="#packages"
          className="view-more-link"
          onClick={handleViewAllClick}
        >
          <small>View more ({packages.length - 2} more) →</small> 
        </a>
      </div>
    );
  }, [packages.length, handleViewAllClick]);

  const renderViewMoreReviews = useMemo(() => {
    if (reviews.length <= 2) return null;
    
    return (
      <div className="view-more-container">
        <a
          href="#reviews"
          className="view-more-link-small"
          onClick={handleViewReviewsClick}
        >
          <small>View more ({reviews.length - 2} more) →</small>
        </a>
      </div>
    );
  }, [reviews.length, handleViewReviewsClick]);

  return (
    <div className="dashboard-content">
      <div className="left-column">
        {renderCreatePackageSection}
        
        <div className="my-packages">
          <div className="section-header-with-status">
            <h2>My Packages</h2>
            {!isVerified && (
              <span className="verification-badge">Verification Required</span>
            )}
          </div>
          <div className="packages-list" id="dashboardPackages">
            {renderDashboardPackages}
          </div>
          {renderViewMorePackages}
        </div>
      </div>
      
      <div className="right-column">
        <div className="booking-overview">
          <div className="section-header">
            <h2>Booking Overview</h2>
          </div>
          <div className="stats">
            <div className="stat">
              <span className="number">{bookingStats.pending}</span>
              <span className="label">Pending</span>
            </div>
            <div className="stat">
              <span className="number">{bookingStats.confirmed}</span>
              <span className="label">Confirmed</span>
            </div>
          </div>
          <a
            href="#bookings"
            className="view-more-link-small"
            onClick={handleViewBookingsClick}
          >
            <small>View more →</small> 
          </a>
        </div>
        
        <div className="earnings-overview">
          <div className="section-header">
            <h2>Earnings Overview</h2>
          </div>
          <span className="earnings-amount">
            {formatCurrency(financeData.totalEarnings)}
          </span>
          <span className="earnings-label">Total Earnings</span>
          <p></p>
          <a
            href="#earnings"
            className="view-more-link-small"
            onClick={handleViewEarningsClick}
          >
            <small>View more →</small>
          </a>
        </div>

        <div className="reviews-overview-dash">
          <div className="section-header">
            <h2>Recent Reviews</h2>
          </div>
          <div className="reviews-list" id="dashboardReviews">
            {renderDashboardReviews}
          </div>
          {renderViewMoreReviews}
        </div>
      </div>
    </div>
  );
};

export default React.memo(Dashboard);